import { ReplaySubject } from "rxjs";
import { Injectable } from "@angular/core";
import { SharedService } from "./shared.service";

@Injectable({
  providedIn: "root",
})
export class LoadingService {
  readonly VINHETA_MIN_MS = 4000;
  private static readonly SPLASH_ID = "orbitta-splash";

  public loading$ = new ReplaySubject<boolean>(1);
  public loadingCart$ = new ReplaySubject<boolean>(1);

  /** Flag síncrona espelhando loadingCart$ — use isCartLoading() em guards de UI. */
  private cartLoading = false;

  constructor(public shared: SharedService) {
    this.showVinheta();
    this.setCartLoading(false);
  }

  /** Atualiza estado de requisição do carrinho (add/update/remove promo etc.). */
  setCartLoading(loading: boolean): void {
    this.cartLoading = loading;
    this.loadingCart$.next(loading);
  }

  isCartLoading(): boolean {
    return this.cartLoading;
  }

  private vinhetaShownAt?: number;
  private hideVinhetaTimer?: ReturnType<typeof setTimeout>;

  showVinheta(): void {
    const alreadyVisible = this.vinhetaShownAt !== undefined;
    this.clearHideVinhetaTimer();
    if (alreadyVisible) {
      return;
    }
    this.vinhetaShownAt = Date.now();
    this.setSplashVisible(true);
    this.applyShellBackground(true);
    this.loading$.next(true);
  }

  hideVinheta(immediate = false): void {
    if (!immediate && this.hideVinhetaTimer) {
      return;
    }

    this.clearHideVinhetaTimer();

    if (immediate || this.vinhetaShownAt === undefined) {
      this.vinhetaShownAt = undefined;
      this.finishVinheta();
      return;
    }

    const remaining = this.VINHETA_MIN_MS - (Date.now() - this.vinhetaShownAt);
    if (remaining <= 0) {
      this.vinhetaShownAt = undefined;
      this.finishVinheta();
      return;
    }

    this.hideVinhetaTimer = setTimeout(() => {
      this.hideVinhetaTimer = undefined;
      this.vinhetaShownAt = undefined;
      this.finishVinheta();
    }, remaining);
  }

  private finishVinheta(): void {
    this.removeSplashElement();
    this.applyShellBackground(false);
    this.loading$.next(false);
  }

  private setSplashVisible(visible: boolean): void {
    const splash = this.ensureSplashElement();
    if (!splash) {
      return;
    }
    splash.style.display = visible ? "block" : "none";
    splash.setAttribute("aria-hidden", visible ? "false" : "true");
  }

  private ensureSplashElement(): HTMLElement | null {
    if (typeof document === "undefined") {
      return null;
    }
    const existing = document.getElementById(LoadingService.SPLASH_ID);
    if (existing) {
      return existing;
    }

    const splash = document.createElement("div");
    splash.id = LoadingService.SPLASH_ID;
    const img = document.createElement("img");
    img.src = "assets/gifs/vinheta.gif";
    img.alt = "";
    img.decoding = "sync";
    img.setAttribute("fetchpriority", "high");
    splash.appendChild(img);
    document.body.prepend(splash);
    return splash;
  }

  private removeSplashElement(): void {
    if (typeof document === "undefined") {
      return;
    }
    document.getElementById(LoadingService.SPLASH_ID)?.remove();
  }

  private applyShellBackground(loading: boolean): void {
    const color = loading ? "#f40000" : "#efefef";
    if (typeof document !== "undefined") {
      document.documentElement.style.backgroundColor = color;
      document.body.style.backgroundColor = color;
    }
  }

  private clearHideVinhetaTimer(): void {
    if (this.hideVinhetaTimer) {
      clearTimeout(this.hideVinhetaTimer);
      this.hideVinhetaTimer = undefined;
    }
  }
}
